import HomeNav from '@/pages/Home/HomeNav';
import { CopyableEmail } from "@/components/common/CopyableEmail";
import HomeFooter from '@/pages/Home/HomeFooter';
import GenieEmbed from '@/components/widget/GenieEmbed';
import { Card, CardContent } from '@/components/ui/card';

const TermsOfService = () => {
  const lastUpdated = "January 16, 2025";

  return (
    <div className="min-h-screen w-full flex flex-col bg-[#F9FAFC]">
      <HomeNav />

      <main className="flex-1 flex flex-col items-center justify-center w-full">
        {/* Hero Section with Background Image */}
        <section className="relative w-full min-h-[400px] flex items-center justify-center text-white">
          {/* Background Image */}
          <div className="absolute inset-0 z-0">
            <img src="/insights-background.webp" alt="Terms of Service background" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-purple-900/80 to-blue-900/80 z-10"></div>
          </div>

          {/* Content */}
          <div className="relative z-20 w-full max-w-screen-xl mx-auto px-8 py-20">
            <div className="text-center">
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight">
                Terms of Service
              </h1>
              <p className="text-xl md:text-2xl text-gray-200 max-w-3xl mx-auto">
                Clear guidelines for using Help Genie's platform and services.
              </p>
              <p className="text-lg text-gray-300 mt-4">
                Last Updated: <span className="font-semibold">{lastUpdated}</span>
              </p>
            </div>
          </div>
        </section>

        {/* Agreement Card */}
        <section className="w-full py-16 md:py-20 bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-white shadow-lg">
              <CardContent className="pt-8 pb-8 px-6 sm:px-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Agreement to Terms</h2>
                <p className="text-gray-700 leading-relaxed text-lg">
                  By accessing or using Help Genie's services, you agree to be bound by these Terms of Service.
                  If you disagree with any part of these terms, you may not access our services.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Content Sections */}
        <section className="w-full py-12 bg-slate-50">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="space-y-12">
            {/* Section 1 */}
            <section>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                1. Description of Services
              </h2>

              <div className="bg-white rounded-lg p-6 sm:p-8 border border-gray-200 shadow-sm">
                <p className="text-gray-700 text-base sm:text-lg mb-4">Help Genie provides:</p>
                <ul className="space-y-3 text-gray-700 text-base sm:text-lg">
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span>Voice AI agent creation and management platform</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span>Conversational AI technology for customer support and engagement</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span>Knowledge base integration and document processing</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span>Phone number provisioning and voice call handling</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span>Analytics and conversation intelligence tools</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span>Marketplace for discovering and deploying pre-built agents</span>
                  </li>
                </ul>
                <p className="text-gray-600 mt-4">
                  We reserve the right to modify, suspend, or discontinue any part of our services at any time
                  with or without notice.
                </p>
              </div>
            </section>

            {/* Section 2 */}
            <section>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                2. Account Registration
              </h2>

              <div className="bg-white rounded-lg p-6 sm:p-8 border border-gray-200 shadow-sm">
                <p className="text-gray-700 text-base sm:text-lg mb-4">To use our services, you must:</p>
                <ul className="space-y-3 text-gray-700 text-base sm:text-lg">
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span>Be at least 18 years old</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span>Provide accurate, current, and complete registration information</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span>Maintain and promptly update your account information</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span>Keep your password secure and confidential</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span>Notify us immediately of any unauthorized access or security breach</span>
                  </li>
                </ul>
                <p className="text-gray-600 mt-4">
                  You are responsible for all activities that occur under your account. We reserve the right
                  to refuse service, terminate accounts, or remove content at our sole discretion.
                </p>
              </div>
            </section>

            {/* Section 3 */}
            <section>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                3. Acceptable Use Policy
              </h2>

              <div className="bg-white rounded-lg p-6 border border-gray-200 mb-6">
                <h3 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-4">
                  3.1 Permitted Uses
                </h3>
                <p className="text-gray-700 text-base sm:text-lg">
                  You may use our services for lawful business purposes, including customer support,
                  lead generation, appointment scheduling, and information delivery.
                </p>
              </div>

              <div className="bg-white rounded-lg p-6 sm:p-8 border border-gray-200 shadow-sm">
                <h3 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-4">
                  3.2 Prohibited Uses
                </h3>
                <p className="text-gray-700 text-base sm:text-lg mb-4">You agree NOT to use our services to:</p>
                <ul className="space-y-3 text-gray-700 text-base sm:text-lg">
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span>Violate any laws, regulations, or third-party rights</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span>Engage in harassment, spam, or unsolicited communications</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span>Impersonate any person or entity or misrepresent your affiliation</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span>Distribute malware, viruses, or harmful code</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span>Attempt to gain unauthorized access to our systems or networks</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span>Scrape, crawl, or use automated means to access our services</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span>Reverse engineer or attempt to extract source code</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span>Use the service for illegal activities or to promote violence, discrimination, or hate speech</span>
                  </li>
                </ul>
              </div>
            </section>

            {/* Section 4 */}
            <section>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                4. Subscription and Billing
              </h2>

              <div className="bg-white rounded-lg p-6 sm:p-8 border border-gray-200 shadow-sm">
                <ul className="space-y-3 text-gray-700 text-base sm:text-lg">
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span><strong>Subscription Plans:</strong> We offer various subscription tiers with different features and usage limits</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span><strong>Billing Cycle:</strong> Subscriptions are billed monthly or annually in advance</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span><strong>Payment Method:</strong> You must provide a valid payment method and authorize automatic recurring charges</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span><strong>Price Changes:</strong> We reserve the right to modify prices with 30 days notice</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span><strong>Usage Limits:</strong> Exceeding plan limits may result in additional charges or service restrictions</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span><strong>Failed Payments:</strong> If payment fails, we may suspend or terminate your account</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span><strong>Refunds:</strong> Subscription fees are non-refundable except as required by law</span>
                  </li>
                </ul>
              </div>
            </section>

            {/* Section 5 */}
            <section>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                5. Cancellation and Termination
              </h2>

              <div className="bg-white rounded-lg p-6 sm:p-8 border border-gray-200 shadow-sm">
                <ul className="space-y-3 text-gray-700 text-base sm:text-lg">
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span><strong>Your Right to Cancel:</strong> You may cancel your subscription at any time from your account settings</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span><strong>Effect of Cancellation:</strong> Access continues until the end of your billing period; no refunds for partial periods</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span><strong>Our Right to Terminate:</strong> We may suspend or terminate your account for violations of these Terms</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span><strong>Data Retention:</strong> After termination, your data may be deleted after 30 days</span>
                  </li>
                </ul>
              </div>
            </section>

            {/* Section 6 */}
            <section>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                6. Intellectual Property Rights
              </h2>

              <div className="bg-white rounded-lg p-6 border border-gray-200 mb-6">
                <h3 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-4">
                  6.1 Our Rights
                </h3>
                <p className="text-gray-700 text-base sm:text-lg mb-4">
                  All rights, title, and interest in and to the Help Genie platform, including all intellectual
                  property rights, are owned by us or our licensors. You may not:
                </p>
                <ul className="space-y-3 text-gray-700 text-base sm:text-lg">
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span>Copy, modify, or create derivative works of our platform</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span>Use our trademarks, logos, or branding without permission</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span>Redistribute or resell our services</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-lg p-6 sm:p-8 border border-gray-200 shadow-sm">
                <h3 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-4">
                  6.2 Your Content
                </h3>
                <p className="text-gray-700 text-base sm:text-lg">
                  You retain ownership of content you upload (prompts, documents, etc.). By uploading content,
                  you grant us a license to use, store, and process your content solely to provide our services.
                  You represent that you have all necessary rights to the content you upload.
                </p>
              </div>
            </section>

            {/* Section 7 */}
            <section>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                7. AI and Voice Technology Disclaimer
              </h2>

              <div className="bg-white rounded-lg p-6 sm:p-8 border border-gray-200 shadow-sm">
                <p className="text-gray-700 text-base sm:text-lg mb-4">
                  Our AI agents use machine learning and may not always produce accurate results:
                </p>
                <ul className="space-y-3 text-gray-700 text-base sm:text-lg">
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span>AI-generated responses may contain errors or inaccuracies</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span>You are responsible for reviewing and validating AI outputs</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span>Voice quality may vary based on network conditions and third-party services</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span>We do not guarantee uninterrupted or error-free service</span>
                  </li>
                </ul>
              </div>
            </section>

            {/* Section 8 */}
            <section>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                8. Limitation of Liability
              </h2>

              <div className="bg-white rounded-lg p-6 sm:p-8 border border-gray-200 shadow-sm">
                <p className="text-gray-700 text-base sm:text-lg mb-4">
                  TO THE MAXIMUM EXTENT PERMITTED BY LAW:
                </p>
                <ul className="space-y-3 text-gray-700 text-base sm:text-lg">
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span>Our services are provided "AS IS" without warranties of any kind</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span>We are not liable for indirect, incidental, special, or consequential damages</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span>Our total liability is limited to the amount you paid in the 12 months prior to the claim</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span>We are not responsible for third-party services, content, or actions</span>
                  </li>
                </ul>
              </div>
            </section>

            {/* Section 9 */}
            <section>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                9. Indemnification
              </h2>

              <div className="bg-white rounded-lg p-6 sm:p-8 border border-gray-200 shadow-sm">
                <p className="text-gray-700 text-base sm:text-lg">
                  You agree to indemnify and hold harmless Help Genie, its affiliates, and employees from any
                  claims, damages, losses, or expenses (including legal fees) arising from:
                </p>
                <ul className="space-y-3 text-gray-600 mt-4">
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span>Your use of our services</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span>Your violation of these Terms</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span>Your content or agents created using our platform</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span>Your violation of any third-party rights</span>
                  </li>
                </ul>
              </div>
            </section>

            {/* Section 10 */}
            <section>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                10. Dispute Resolution
              </h2>

              <div className="bg-white rounded-lg p-6 sm:p-8 border border-gray-200 shadow-sm">
                <ul className="space-y-3 text-gray-700 text-base sm:text-lg">
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span><strong>Informal Resolution:</strong> Contact us first to attempt informal resolution</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span><strong>Governing Law:</strong> These Terms are governed by the laws of [Your Jurisdiction]</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span><strong>Arbitration:</strong> Disputes will be resolved through binding arbitration</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span><strong>Class Action Waiver:</strong> You agree not to participate in class action lawsuits</span>
                  </li>
                </ul>
              </div>
            </section>

            {/* Section 11 */}
            <section>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                11. Changes to Terms
              </h2>

              <div className="bg-white rounded-lg p-6 sm:p-8 border border-gray-200 shadow-sm">
                <p className="text-gray-700 text-base sm:text-lg">
                  We may modify these Terms at any time. Material changes will be notified via email or through
                  our platform. Continued use of our services after changes constitutes acceptance of the new Terms.
                  If you do not agree to the changes, you must stop using our services and cancel your account.
                </p>
              </div>
            </section>

            {/* Section 12 */}
            <section>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                12. Miscellaneous
              </h2>

              <div className="bg-white rounded-lg p-6 sm:p-8 border border-gray-200 shadow-sm">
                <ul className="space-y-3 text-gray-700 text-base sm:text-lg">
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span><strong>Entire Agreement:</strong> These Terms constitute the entire agreement between you and Help Genie</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span><strong>Severability:</strong> If any provision is invalid, the remaining provisions remain in effect</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span><strong>Waiver:</strong> Failure to enforce a provision does not waive our right to do so later</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span><strong>Assignment:</strong> You may not transfer your rights; we may assign our rights without restriction</span>
                  </li>
                </ul>
              </div>
            </section>

            {/* Section 13 */}
            <section>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                13. Contact Information
              </h2>

              <div className="bg-white rounded-lg p-6 sm:p-8 border border-gray-200 shadow-sm">
                <p className="text-gray-700 text-base sm:text-lg mb-4">
                  For questions about these Terms, please contact us:
                </p>
                <ul className="space-y-2 text-gray-700 text-base sm:text-lg">
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span>
                      By email:{' '}
                      <CopyableEmail email="legal@helpgenie.com" href="mailto:legal@helpgenie.com" className="text-genie-primary font-medium" />
                    </span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span>
                      By visiting our support page:{' '}
                      <a href="https://helpgenie.ai/contact" className="text-purple-600 hover:text-purple-700 font-medium">
                        helpgenie.ai/contact
                      </a>
                    </span>
                  </li>
                </ul>
              </div>
            </section>
            </div>
          </div>
        </section>
      </main>

      <HomeFooter />
      <GenieEmbed position="bottom-right" />
    </div>
  );
};

export default TermsOfService;
